'use client';

import { Home, Refresh } from '@mui/icons-material';
import { AppBar, Box, Button, Container, Toolbar, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  page?: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can log the error to an error reporting service here
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box data-component="ErrorBoundary">
          {this.props.page && (
            <header>
              <AppBar data-component="ErrNavBar" position="fixed">
                <Container maxWidth="xl" sx={{ py: 1.5 }}>
                  <Toolbar sx={{ py: 1.5 }}>
                    <Link href="/">
                      <Image src="/assets/logo-white.svg" alt="Lend Logo" width={180} height={50} />
                    </Link>
                  </Toolbar>
                </Container>
              </AppBar>
            </header>
          )}
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: this.props.page ? 'calc(100vh - 120px)' : '60vh',
                textAlign: 'center',
                gap: 3,
                py: 8,
                mt: 11,
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                Oops! Something went wrong
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ maxWidth: '600px' }}
              >
                We're sorry, but something unexpected happened. This is likely a temporary issue.
                You can try refreshing the page or return to the homepage.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleRefresh}
                  startIcon={<Refresh />}
                >
                  Refresh Page
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleGoHome}
                  startIcon={<Home />}
                >
                  Return to Home
                </Button>
              </Box>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    mt: 4,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    maxWidth: '100%',
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                    {this.state.error.toString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
